/**
 * ApplianceSignIn — the one-time sign-in link printed by a customer-hosted appliance.
 *
 * URL: /appliance/sign-in#token=<one-time token>
 *
 * The token is read from the FRAGMENT, never the query string: browsers do not send a fragment to
 * the server, so it never reaches an access log. After redeeming, navigation replaces this history
 * entry. The backend route 404s on hosted PRISM (APPLIANCE_MODE unset), so this page does nothing
 * there but show the error.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { redeemApplianceToken } from '../services/api';

function tokenFromHash(hash: string): string | null {
  const match = /^#token=([A-Za-z0-9_-]{1,128})$/.exec(hash.slice(0, 200));
  return match ? match[1] : null;
}

export default function ApplianceSignIn() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // A single-use token must not be spent twice by a double-invoked effect.
    if (started.current) return;
    started.current = true;

    const token = tokenFromHash(location.hash);
    if (!token) {
      setError('This sign-in link is incomplete. Copy the whole link from the appliance output.');
      return;
    }
    // redeemApplianceToken persists prism_token / prism_user, as the other login functions do.
    redeemApplianceToken(token)
      .then(() => navigate('/', { replace: true }))
      .catch(() =>
        setError('This sign-in link is invalid, already used, or expired. Ask for a new one.')
      );
  }, [location.hash, navigate]);

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2, p: 3 }}>
        <Alert role="alert" severity="error" sx={{ maxWidth: 560 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
          An administrator of this appliance can issue a new link by running, inside the app
          container: <code>node /app/backend/dist/appliance/issueAdminTokenCli.js &lt;email&gt;</code>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Signing you in…
      </Typography>
    </Box>
  );
}
